import React, { useRef, useState, useCallback, useEffect } from "react";
import { useGesture } from "@use-gesture/react";
import type {
  WeeklyAvailability,
  DayOfWeek,
  TimeSlot,
} from "@applicant-reviewer/api/availability";
import { SLOT_STATUS, DAYS_OF_WEEK } from "@applicant-reviewer/api/availability";
import ScheduleSummaryModal from "./ScheduleSummaryModal";
import ApplicationWizard from "../../apps/candidate/components/application/ApplicationWizard";
import type { Answer } from "../../apps/candidate/components/application/StepQuestions";
import { MOCK_JOB } from "../../data/mockJob";
import CompanyLogo from "../ui/CompanyLogo";

type WizardStep = "preferred" | "available";
type PaintIntent = "paint" | "erase";

// Business Hours Constants
const BUSINESS_START_SLOT = 12; // 6 AM (6 * 2)
const BUSINESS_END_SLOT = 44; // 10 PM (22 * 2)
const TOTAL_SLOTS = 48; // 24 hours

// Grid Dimensions (per Figma)
const CELL_HEIGHT = 24; // px (per Figma: h-[24px])
const TIME_COLUMN_WIDTH = 48; // px (adjusted to fit all 7 days)
const HOUR_LINE_EXTENSION = 7; // px (extends left of cells on even hours)

// Scroll restriction: 0.5 hour before/after store hours
const RESTRICT_START_SLOT = 11; // 5:30 AM (0.5 hour before 6 AM)
const RESTRICT_END_SLOT = 45; // 10:30 PM (0.5 hour after 10 PM)

// Top bar height for scroll calculations
const TOP_BAR_HEIGHT = 140; // Approximate height of header + instruction bar

interface ZoomableScheduleCanvasProps {
  availability: WeeklyAvailability;
  onChange: (availability: WeeklyAvailability) => void;
  hourlyRate?: number;
  onSave?: () => void;
  readOnly?: boolean;
  // Header props
  companyName?: string;
  companyLogo?: string;
  location?: string;
  onWatchVideo?: () => void;
  // Navigation
  onBack?: () => void;
  // Manager Mode
  isManagerMode?: boolean;
}

// Helper: Convert slot index to time string (full uppercase with space)
function slotIndexToTime(index: number): string {
  const hour = Math.floor(index / 2);
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour} ${period}`;
}

// Helper: Get cell key for tracking
function getCellKey(dayIndex: number, slotIndex: number): string {
  return `${dayIndex}-${slotIndex}`;
}

// Helper: Get color class for slot status (using Figma design colors)
function getStatusColor(status: number, slotIndex: number): string {
  // Outside business hours → closed (gray)
  if (slotIndex < BUSINESS_START_SLOT || slotIndex > BUSINESS_END_SLOT) {
    return "bg-[#eeeeee]"; // dashboard-ui-3-color (closed)
  }
  
  // Inside business hours → Figma design colors
  switch (status) {
    case SLOT_STATUS.PREFERRED:
      return "bg-[#effdec]"; // dashboard-green-faint-color (light green)
    case SLOT_STATUS.AVAILABLE:
      return "bg-[#e8f4ff]"; // light blue for available
    default:
      return "bg-white";
  }
}


export default function ZoomableScheduleCanvas({
  availability,
  onChange,
  hourlyRate = 17,
  onSave,
  readOnly = false,
  companyName,
  companyLogo,
  location,
  onWatchVideo,
  onBack,
  isManagerMode = false,
}: ZoomableScheduleCanvasProps) {
  // Wizard step state
  const [step, setStep] = useState<WizardStep>("preferred");

  // Modal state
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  
  // Application wizard state
  const [showApplicationWizard, setShowApplicationWizard] = useState(false);

  // View restriction state (debug toggle)
  const [restrictView, setRestrictView] = useState(true);

  // Gesture state tracking
  const isScrollingRef = useRef(false); // Lock for 2-finger scroll
  const paintIntentRef = useRef<PaintIntent>("paint"); // Determined at drag start

  // Paint tracking
  const paintedCellsRef = useRef<Set<string>>(new Set());

  // Container ref (native scroll container)
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll state (for edge-based scrolling during drag)
  const autoScrollFrameRef = useRef<number | null>(null);
  const autoScrollSpeedRef = useRef<number>(0);

  // Render slots based on view mode
  const visibleSlots = restrictView 
    ? RESTRICT_END_SLOT - RESTRICT_START_SLOT + 1 
    : TOTAL_SLOTS;
  const startSlot = restrictView ? RESTRICT_START_SLOT : 0;

  // Update availability helper
  const updateSlot = useCallback(
    (dayIndex: number, slotIndex: number, newStatus: number) => {
      const newAvailability = [...availability];
      newAvailability[dayIndex].slots[slotIndex].status = newStatus as 0 | 1 | 2;
      onChange(newAvailability);
    },
    [availability, onChange]
  );

  // Paint slot using current intent (determined at drag start)
  const paintSlot = useCallback(
    (dayIndex: number, slotIndex: number) => {
      const cellKey = getCellKey(dayIndex, slotIndex);

      // Skip if already painted in this gesture
      if (paintedCellsRef.current.has(cellKey)) return;

      const currentStatus = availability[dayIndex].slots[slotIndex].status;

      // STEP 2 CONSTRAINT: Don't allow painting on locked green cells
      if (step === "available" && currentStatus === SLOT_STATUS.PREFERRED) {
        return; // Green cells are locked in step 2
      }

      // Apply paint intent based on current step
      let newStatus: number;
      if (paintIntentRef.current === "erase") {
        newStatus = SLOT_STATUS.UNAVAILABLE;
      } else {
        // Paint with current step's color
        newStatus = step === "preferred" ? SLOT_STATUS.PREFERRED : SLOT_STATUS.AVAILABLE;
      }

      // Only update if different
      if (currentStatus !== newStatus) {
        updateSlot(dayIndex, slotIndex, newStatus);
      }

      paintedCellsRef.current.add(cellKey);
    },
    [availability, step, updateSlot]
  );

  // Handle cell click/tap (fallback for taps that don't trigger drag)
  const handleCellClick = useCallback((dayIndex: number, slotIndex: number, event: React.MouseEvent) => {
    event.stopPropagation();
    
    // Read-only mode: disable painting
    if (readOnly) return;
    
    console.log('Cell clicked:', dayIndex, slotIndex);
    
    const currentStatus = availability[dayIndex].slots[slotIndex].status;
    
    // STEP 2 CONSTRAINT: Don't allow clicking locked green cells
    if (step === "available" && currentStatus === SLOT_STATUS.PREFERRED) {
      console.log('Green cell locked in step 2');
      return;
    }
    
    // Determine intent: If step's color matches current status, erase. Otherwise, paint.
    const stepStatus = step === "preferred" ? SLOT_STATUS.PREFERRED : SLOT_STATUS.AVAILABLE;
    paintIntentRef.current = currentStatus === stepStatus ? "erase" : "paint";
    
    console.log('Click intent:', paintIntentRef.current, 'step:', step, 'current:', currentStatus);
    
    paintedCellsRef.current.clear(); // Clear previous gesture
    paintSlot(dayIndex, slotIndex);
    paintedCellsRef.current.clear(); // Clear after single paint
  }, [paintSlot, step, availability, readOnly]);

  // Hit detection: Find cell under pointer using elementFromPoint (works with scroll/zoom)
  const findCellAtPoint = useCallback((x: number, y: number): { dayIndex: number; slotIndex: number } | null => {
    // Use document.elementFromPoint for accurate hit detection
    const element = document.elementFromPoint(x, y);
    
    if (element) {
      // Check for data attributes on the element or its parents
      let currentElement: Element | null = element;
      while (currentElement && currentElement !== containerRef.current) {
        const dayIndex = currentElement.getAttribute('data-day-index');
        const slotIndex = currentElement.getAttribute('data-slot-index');
        
        if (dayIndex !== null && slotIndex !== null) {
          return {
            dayIndex: parseInt(dayIndex, 10),
            slotIndex: parseInt(slotIndex, 10),
          };
        }
        
        currentElement = currentElement.parentElement;
      }
    }
    
    return null;
  }, []);

  // Auto-scroll functions for edge-based scrolling
  const startAutoScroll = useCallback((speed: number) => {
    // Update speed
    autoScrollSpeedRef.current = speed;
    
    // Start animation loop if not already running
    if (autoScrollFrameRef.current === null) {
      const scroll = () => {
        if (containerRef.current && autoScrollSpeedRef.current !== 0) {
          // Apply scroll
          const currentScroll = containerRef.current.scrollTop;
          const newScroll = currentScroll + autoScrollSpeedRef.current;
          
          // Get scroll bounds
          const maxScroll = containerRef.current.scrollHeight - containerRef.current.clientHeight;
          
          // Clamp to valid range
          containerRef.current.scrollTop = Math.max(0, Math.min(maxScroll, newScroll));
          
          // Continue animation
          autoScrollFrameRef.current = requestAnimationFrame(scroll);
        }
      };
      autoScrollFrameRef.current = requestAnimationFrame(scroll);
    }
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current !== null) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
      autoScrollSpeedRef.current = 0;
    }
  }, []);

  // Gesture handling (SIMPLIFIED: 2-finger scroll, 1-finger smart paint)
  useGesture(
    {
      // Drag: MANUAL SCROLL (2-finger) OR SMART PAINT (1-finger)
      onDrag: ({ event, xy: [x, y], delta: [, dy], first, last, touches }) => {
        event.preventDefault(); // Block native touch handling

        // Get pointer count
        const pointerCount = touches ?? 1;

        // TWO FINGERS: Manual scroll (update scrollTop)
        if (pointerCount === 2) {
          if (first) {
            isScrollingRef.current = true;
            console.log('📜 2-finger scroll started');
          }
          
          if (containerRef.current) {
            // Calculate new scroll position
            let newScrollTop = containerRef.current.scrollTop - dy;
            
            // Get actual scroll dimensions from container
            const scrollHeight = containerRef.current.scrollHeight;
            const clientHeight = containerRef.current.clientHeight;
            const maxPossibleScroll = scrollHeight - clientHeight;
            
            // ENFORCE SCROLL BOUNDS based on restrictView
            // Note: In both modes, we render from slot 0 upwards, but in restricted mode
            // slot 0 represents RESTRICT_START_SLOT (5:30 AM), not midnight
            const minScroll = 0;
            const maxScroll = maxPossibleScroll;
            
            // Clamp scroll position
            newScrollTop = Math.max(minScroll, Math.min(maxScroll, newScrollTop));
            
            containerRef.current.scrollTop = newScrollTop;
          }
          
          if (last) {
            isScrollingRef.current = false;
            console.log('📜 2-finger scroll ended');
          }
          
          return; // Don't paint
        }

        // ONE FINGER: Smart toggle paint (erase if step color matches, paint otherwise)
        if (pointerCount === 1) {
          // Read-only mode: disable painting
          if (readOnly) return;
          
          if (first) {
            paintedCellsRef.current.clear();
            
            // Determine intent based on INITIAL cell under finger
            const initialCell = findCellAtPoint(x, y);
            if (initialCell) {
              const initialStatus = availability[initialCell.dayIndex].slots[initialCell.slotIndex].status;
              
              // STEP 2 CONSTRAINT: Don't allow painting locked green cells
              if (step === "available" && initialStatus === SLOT_STATUS.PREFERRED) {
                console.log('🎨 Paint blocked - green cell locked in step 2');
                return;
              }
              
              const stepStatus = step === "preferred" ? SLOT_STATUS.PREFERRED : SLOT_STATUS.AVAILABLE;
              
              // If step color matches initial cell, intent is ERASE. Otherwise, PAINT.
              paintIntentRef.current = initialStatus === stepStatus ? "erase" : "paint";
              
              console.log('🎨 Paint started - Intent:', paintIntentRef.current, 'Step:', step, 'Initial cell:', initialStatus);
              
              // CRITICAL: Paint the initial cell immediately (fixes single-tap issue)
              paintSlot(initialCell.dayIndex, initialCell.slotIndex);
            } else {
              // Default to paint if no cell found
              paintIntentRef.current = "paint";
            }
          }

          // Continue painting cells as finger moves
          const cell = findCellAtPoint(x, y);
          if (cell) {
            paintSlot(cell.dayIndex, cell.slotIndex);
          }

          // EDGE-BASED AUTO-SCROLL: Scroll when dragging near top or bottom edge
          if (containerRef.current) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const edgeZone = 80; // px from edge to trigger auto-scroll
            const maxScrollSpeed = 8; // max px per frame
            
            // Calculate Y position relative to container
            const relativeY = y - containerRect.top;
            const containerHeight = containerRect.height;
            
            // Near top edge - scroll up (negative speed)
            if (relativeY < edgeZone && relativeY >= 0) {
              const intensity = 1 - (relativeY / edgeZone); // 0-1, higher when closer to edge
              const speed = -maxScrollSpeed * intensity;
              startAutoScroll(speed);
            }
            // Near bottom edge - scroll down (positive speed)
            else if (relativeY > containerHeight - edgeZone && relativeY <= containerHeight) {
              const distanceFromBottom = containerHeight - relativeY;
              const intensity = 1 - (distanceFromBottom / edgeZone); // 0-1, higher when closer to edge
              const speed = maxScrollSpeed * intensity;
              startAutoScroll(speed);
            }
            // In middle zone - stop auto-scroll
            else {
              stopAutoScroll();
            }
          }

          if (last) {
            paintedCellsRef.current.clear();
            stopAutoScroll(); // Stop auto-scroll when drag ends
            console.log('🎨 Paint ended');
          }
        }
      },
    },
    {
      target: containerRef,
      eventOptions: { passive: false }, // Allows preventDefault
      drag: {
        filterTaps: true,
        threshold: 5, // 5px to trigger drag
        pointers: undefined, // Allow 1 AND 2 finger drags
      },
    }
  );

  // Auto-scroll to 8 AM on mount
  useEffect(() => {
    if (containerRef.current) {
      const eightAMSlot = 16; // 8 AM = 8 * 2
      const scrollPosition = eightAMSlot * CELL_HEIGHT;
      containerRef.current.scrollTop = scrollPosition;
    }
  }, []); // Run once on mount

  // Calculate stats
  const availableSlots = availability.reduce(
    (sum, day) => sum + day.slots.filter((s) => s.status === SLOT_STATUS.AVAILABLE).length,
    0
  );
  const preferredSlots = availability.reduce(
    (sum, day) => sum + day.slots.filter((s) => s.status === SLOT_STATUS.PREFERRED).length,
    0
  );
  const availableHours = availableSlots * 0.5;
  const preferredHours = preferredSlots * 0.5;

  // Modal handlers
  const handleOpenSummary = () => {
    // Call onSave callback to navigate to summary screen
    onSave?.();
  };

  const handleCloseSummary = () => {
    setShowSummaryModal(false);
  };

  const handleStartApplication = () => {
    setShowSummaryModal(false);
    setShowApplicationWizard(true);
  };

  const handleApplicationComplete = (data: { answers: Answer[] }) => {
    console.log("📤 APPLICATION COMPLETE:");
    console.log({
      schedule: availability,
      answers: data.answers,
    });
    
    setShowApplicationWizard(false);
    
    if (onSave) {
      onSave();
    }
  };

  const handleCancelApplication = () => {
    setShowApplicationWizard(false);
    setShowSummaryModal(true); // Return to summary
  };

  // Grid uses dynamic width - columns fill available space

  return (
    <div className="w-full flex flex-col bg-white relative" style={{ height: isManagerMode ? '100%' : '100dvh' }}>
      {/* SECTION A: HEADER (Exact match with JobLandingPage - no pixel movement) */}
      {(companyName || companyLogo) && (
        <div className="px-5 pt-[14px] pb-[14px] flex-shrink-0">
          <div className="flex items-start gap-3">
            {/* Company Logo */}
            <CompanyLogo
              src={companyLogo}
              alt={companyName || ''}
              className="w-[57px] h-[57px] rounded-lg"
            />

            {/* Company Info */}
            <div className="flex-1">
              <h2 className="text-[19px] font-bold text-[#28292d] leading-[1.4]">
                {companyName}
              </h2>
              <p className="text-[17px] text-[#28292d] leading-[1.4]">
                {location}
              </p>
            </div>

            {/* Watch Video Link */}
            {onWatchVideo && (
              <button
                onClick={onWatchVideo}
                className="self-center flex items-center gap-1 text-[16px] text-[#0156FF] font-normal"
              >
                <svg className="w-[16px] h-[16px]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
                Watch Video
              </button>
            )}
          </div>
        </div>
      )}

      {/* Gray Divider (exact match with JobLandingPage) */}
      <div className="border-t border-[#b9babf] mx-5 flex-shrink-0" />

      {/* SECTION B: INSTRUCTION + ACTION BAR (TOP - per Figma design) */}
      {!readOnly && (
        <div className="px-5 pt-5 pb-3 flex items-center justify-between flex-shrink-0">
          {isManagerMode ? (
            /* MANAGER MODE: Single step (Green only) */
            <>
              <p className="text-[14px] font-bold text-[#28292d]">
                Tap the times you can interview
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={onBack}
                  className="bg-[#8E8E8F] text-white text-[18px] font-bold rounded-full active:scale-[0.98] transition-transform flex items-center justify-center"
                  style={{ width: '80px', height: '40px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={onSave}
                  className="bg-[#0156FF] text-white text-[18px] font-bold rounded-full active:scale-[0.98] transition-transform flex items-center justify-center"
                  style={{ width: '80px', height: '40px' }}
                >
                  Save
                </button>
              </div>
            </>
          ) : step === "preferred" ? (
            /* APPLICANT MODE: Step 1 */
            <>
              <p className="text-[14px] font-bold text-[#28292d]">
                Tap the hours you prefer to work
              </p>
              <div className="flex items-center gap-2">
                {onBack && (
                  <button
                    onClick={onBack}
                  className="bg-[#8E8E8F] text-white text-[18px] font-bold rounded-full active:scale-[0.98] transition-transform flex items-center justify-center"
                  style={{ width: '80px', height: '40px' }}
                >
                  Back
                </button>
              )}
              <button
                onClick={() => setStep("available")}
                className="bg-[#0156FF] text-white text-[18px] font-bold rounded-full active:scale-[0.98] transition-transform flex items-center justify-center"
                style={{ width: '80px', height: '40px' }}
              >
                Next
              </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[14px] font-bold text-[#28292d]">
                Add other hours you can work
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setStep("preferred")}
                  className="bg-[#8E8E8F] text-white text-[18px] font-bold rounded-full active:scale-[0.98] transition-transform flex items-center justify-center"
                  style={{ width: '80px', height: '40px' }}
                >
                  Back
                </button>
                <button
                  onClick={handleOpenSummary}
                  className="bg-[#0156FF] text-white text-[18px] font-bold rounded-full active:scale-[0.98] transition-transform flex items-center justify-center"
                  style={{ width: '80px', height: '40px' }}
                >
                  Save
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Debug Toggle */}
      <button
        onClick={() => setRestrictView((prev) => !prev)}
        className="absolute top-2 right-2 z-40 px-2 py-1 bg-purple-600/90 hover:bg-purple-700 text-white text-[10px] font-semibold rounded shadow-lg transition-colors"
      >
        {restrictView ? "🔒 Restricted" : "🌍 Full 24h"}
      </button>

      {/* SECTION C: DAY HEADERS ROW (Fixed) */}
      <div 
        className="flex-shrink-0 bg-white px-5 mt-[15px]"
      >
        <div 
          className="grid gap-0 w-full" 
          style={{ 
            gridTemplateColumns: `${TIME_COLUMN_WIDTH}px ${HOUR_LINE_EXTENSION}px repeat(7, 1fr)`,
          }}
        >
          {/* Empty cell for time column */}
          <div className="h-[21px]" />
          
          {/* Empty cell for extension column */}
          <div className="h-[21px]" />
          
          {/* Day headers */}
          {DAYS_OF_WEEK.map((day, index) => (
            <div
              key={`header-${day}`}
              className="h-[21px] flex items-center justify-center text-[13px] font-bold text-[#28292d]"
              style={{ 
                borderTop: '0.25px solid #b9babf',
                borderBottom: '0.25px solid #b9babf',
                borderRight: '0.25px solid #b9babf',
                borderLeft: index === 0 ? '0.25px solid #b9babf' : 'none',
              }}
            >
              {day}
            </div>
          ))}
        </div>
      </div>

      {/* SECTION D: SCROLLABLE GRID */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden bg-white px-5"
        style={{
          cursor: "crosshair",
          touchAction: "none",
          overscrollBehavior: "contain",
        }}
      >
        {/* Schedule Grid */}
        <div
          className="grid gap-0 bg-white relative w-full"
          style={{
            gridTemplateColumns: `${TIME_COLUMN_WIDTH}px ${HOUR_LINE_EXTENSION}px repeat(7, 1fr)`,
          }}
        >
          {/* Time Slots Grid - ALL 48 SLOTS (24 hours) */}
          {Array.from({ length: visibleSlots }).map((_, index) => {
            const slotIndex = startSlot + index; // Actual slot index in the 48-slot array
            // Show time label at start of each hour (even slots: 0=12AM, 12=6AM, etc.)
            const isHourStart = slotIndex % 2 === 0;
            const showTimeLabel = isHourStart;
            // Hour line extension appears at odd slots (hour boundaries: 1=12:30 divides 12-1, etc.)
            const showHourLineExtension = slotIndex % 2 === 1;
            
            return (
              <React.Fragment key={`row-${slotIndex}`}>
                {/* TIME COLUMN (left) */}
                <div
                  className="relative bg-white z-10"
                  style={{ 
                    height: `${CELL_HEIGHT}px`,
                    width: `${TIME_COLUMN_WIDTH}px`,
                  }}
                >
                  {/* Time label at hour start, centered in the column */}
                  {showTimeLabel && (
                    <span 
                      className="absolute left-0 right-0 text-center text-[13px] font-bold text-[#28292d] leading-none whitespace-nowrap z-10"
                      style={{
                        top: '-6px',
                      }}
                    >
                      {slotIndexToTime(slotIndex)}
                    </span>
                  )}
                </div>
                
                {/* EXTENSION COLUMN (7px) - Shows horizontal line at even hours */}
                <div
                  className="bg-white"
                  style={{ 
                    height: `${CELL_HEIGHT}px`,
                    width: `${HOUR_LINE_EXTENSION}px`,
                    borderBottom: showHourLineExtension ? '0.25px solid #b9babf' : 'none',
                  }}
                />
                
                {/* DAY COLUMNS (7 columns - dynamic width) */}
                {DAYS_OF_WEEK.map((day, dayIndex) => {
                  const slot = availability[dayIndex].slots[slotIndex];
                  const isPreferredSlot = slot.status === SLOT_STATUS.PREFERRED;
                  const isLockedInStep2 = step === "available" && isPreferredSlot;
                  const isFirstColumn = dayIndex === 0;
                  const isLastColumn = dayIndex === 6;

                  return (
                    <div
                      key={getCellKey(dayIndex, slotIndex)}
                      data-day-index={dayIndex}
                      data-slot-index={slotIndex}
                      onClick={(e) => handleCellClick(dayIndex, slotIndex, e)}
                      className={`
                        relative flex items-center justify-center
                        transition-all duration-75
                        ${getStatusColor(slot.status, slotIndex)}
                        ${isLockedInStep2 ? 'cursor-not-allowed' : 'hover:opacity-80 cursor-pointer'}
                      `}
                      style={{ 
                        height: `${CELL_HEIGHT}px`,
                        borderBottom: '0.25px solid #b9babf',
                        borderRight: '0.25px solid #b9babf',
                        borderLeft: isFirstColumn ? '0.25px solid #b9babf' : 'none',
                        opacity: isLockedInStep2 ? 0.7 : 1,
                      }}
                    >
                      {isLockedInStep2 && (
                        <svg
                          className="w-3 h-3 text-green-900 absolute"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Summary Modal - REMOVED: Now navigating to ScheduleSummaryPage instead */}
      {/* {showSummaryModal && (
        <ScheduleSummaryModal
          availability={availability}
          hourlyRate={hourlyRate}
          onEdit={handleCloseSummary}
          onStartApplication={handleStartApplication}
        />
      )} */}

      {/* Application Wizard */}
      {showApplicationWizard && (
        <ApplicationWizard
          questions={MOCK_JOB.questions}
          onComplete={handleApplicationComplete}
          onCancel={handleCancelApplication}
        />
      )}
    </div>
  );
}
